import React, { useEffect, useMemo } from 'react';
import { ICONS, USER_ROLES, USER_TYPES } from '../../common/constants';
import { variables } from '../../Tenants';
import { onCheckDocument } from '../../helpers/CommonHelpers';
import Col from '../Col';
import File from '../File';
import { Heading } from '../Form';
import IcoMoon from '../IcoMoon';
import Icon from '../Icon';
import Input from '../Input';
import Row from '../Row';
import Select from '../Select';
import Tile from '../Tile';
import Toggle from '../Toggle';
import CheckBox from '../Checkbox';
import { Multiselect } from 'multiselect-react-dropdown';
import { toast } from 'react-toastify';

/*
 * FIX: Direct import of IcoMoon styles
 * -----------------------------------
 * PROBLEM: In CRA, styles imported in index.scss would be processed early in the build
 * and available to all components. In Vite, style processing order can differ, leading
 * to components rendering before their styles are fully loaded.
 *
 * SOLUTION: Import the icomoon styles directly in the component that needs them.
 * This ensures the styles are bundled with this component and available when it renders.
 * This is a "belt and suspenders" approach that guarantees styles are available.
 */
import '../../styles/icomoon/style.scss';
import {
  ContactFormData,
  ContactFormErrors,
  User,
  Property,
  ServiceType,
  DetailedSuite,
  PropertyListItem,
  SuiteListItem,
  LocationPrediction,
} from 'src/components/ContactForm/types';
import { getServiceProviderProperties } from 'src/services/client';

// Todo: Properly migrate all of these types from Vanilla JS to TypeScript.
interface ContactFormContentProps {
  // Main data objects
  contactFormData: ContactFormData;
  properties: Property[];
  propertySuites: DetailedSuite[];
  user: User;
  types: ServiceType[];
  errors: ContactFormErrors;

  // Selection lists.
  propertiesList: PropertyListItem[];
  suitesList: SuiteListItem[];

  // Location search.
  predictions: LocationPrediction[];
  location: string;

  // Flags and configurations.
  isFromPropertyDeveloper?: boolean;
  filteredSuites?: DetailedSuite[];

  // Callback functions.
  onSetContactFormData: (data: Partial<ContactFormData>) => void;
  onErrorEmpty: (field: string) => void;
  disableEmailField: () => boolean;
  checkBoxValue: () => boolean;
  getLocation: (value: string) => void;
  onClickLocation: (description: string) => void;
  onFetchPropertySuites: (
    propertyId: string,
    userId: string,
    role: string
  ) => void;
  clearSuites: () => void;
  selectionForProperties: (
    e: PropertyListItem | PropertyListItem[],
    action: 'add' | 'remove'
  ) => void;
  filterList: (e: SuiteListItem | SuiteListItem[]) => void;
  contactType: (role: string) => string;
}

// Internal component for contact's basic information.
const ContactBasicInfo = ({
  contactFormData,
  onSetContactFormData,
  errors,
  onErrorEmpty,
  disableEmailField,
  checkBoxValue,
}: ContactFormContentProps) => (
  <>
    <Heading>Contact Information</Heading>
    {/* Name Fields. */}
    <Row>
      <Col xs="12" md="6">
        <Input
          value={contactFormData.firstname}
          onChange={(e: any) =>
            onSetContactFormData({ firstname: e.target.value })
          }
          label="First Name"
          placeholder="First Name"
          block
          required
          errors={errors.firstname}
        />
      </Col>
      <Col xs="12" md="6">
        <Input
          value={contactFormData.lastname}
          onChange={(e: any) =>
            onSetContactFormData({ lastname: e.target.value })
          }
          label="Last Name"
          placeholder="Last Name"
          block
          required
          errors={errors.lastname}
        />
      </Col>
    </Row>
    {/* Email Field. */}
    <Input
      value={contactFormData.email}
      onChange={(e: any) => {
        onErrorEmpty('email');
        return onSetContactFormData({ email: e.target.value });
      }}
      label="Email"
      placeholder="Email"
      block
      required
      errors={errors.email}
      disabled={disableEmailField()}
    />
    {/* Activation Email Checkbox. */}
    <CheckBox
      value={checkBoxValue()}
      label="Send Activation Email"
      onChange={() =>
        onSetContactFormData({
          sendActivationEmail:
            contactFormData.sendActivationEmail &&
            contactFormData.sendActivationEmail === true
              ? false
              : true,
        })
      }
    />
    {/* Phone Field. */}
    <Input
      value={contactFormData.phone}
      onChange={(e: any) => {
        onErrorEmpty('phone');
        return onSetContactFormData({ phone: e.target.value });
      }}
      label="Phone Number"
      placeholder="Phone Number"
      block
      required
      errors={errors.phone}
    />
    {/* Profile Picture. */}
    <File
      label="Profile Picture"
      onSelect={(file) =>
        onCheckDocument(file) && onSetContactFormData({ selectedImage: file })
      }
      files={contactFormData.selectedImage ? contactFormData.selectedImage : []}
      image={contactFormData.profileImage}
      errors={errors.profileImage}
      type="image"
    />
  </>
);

// Internal component for contact's address.
const ContactAddress = ({
  contactFormData,
  onSetContactFormData,
  errors,
  onErrorEmpty,
  getLocation,
  predictions,
  onClickLocation,
  location,
}: ContactFormContentProps) => {
  const address =
    contactFormData.address != null ? contactFormData.address : {};

  return (
    <>
      {/* Address Search. */}
      <Input
        value={location}
        onChange={(e) => getLocation(e.target.value)}
        label="Search Location"
        block
        placeholder="Search Location"
      />
      {/* Address Predictions. */}
      {predictions.map((item: LocationPrediction, i: number) => (
        <div
          className="location_search"
          onClick={() => onClickLocation(item.description)}
          key={i}
        >
          {item.description}
        </div>
      ))}
      {/* Address Line. */}
      <Input
        value={
          address.hasOwnProperty('addressLine1') ? address.addressLine1 : ''
        }
        onChange={(e: any) =>
          onSetContactFormData({
            address: { ...address, addressLine1: e.target.value },
          })
        }
        label="Address"
        placeholder="eg., 123 Example Street"
        block
      />
      {/* City, Province, Postal Code. */}
      <Row>
        <Col xs="12" md="4">
          <Input
            value={address.hasOwnProperty('city') ? address.city : ''}
            onChange={(e: any) =>
              onSetContactFormData({
                address: { ...address, city: e.target.value },
              })
            }
            label="City"
            placeholder="eg., Vancouver"
            block
          />
        </Col>
        <Col xs="12" md="4">
          <Input
            value={address.hasOwnProperty('province') ? address.province : ''}
            onChange={(e: any) =>
              onSetContactFormData({
                address: { ...address, province: e.target.value },
              })
            }
            label="Province"
            placeholder="eg., BC"
            block
          />
        </Col>
        <Col xs="12" md="4">
          <Input
            value={
              address.hasOwnProperty('postalCode') ? address.postalCode : ''
            }
            onChange={(e: any) => {
              onErrorEmpty('postalCode');
              return onSetContactFormData({
                address: { ...address, postalCode: e.target.value },
              });
            }}
            label="Postal Code"
            placeholder="eg., A0A 0A0"
            block
            errors={errors.postalCode}
          />
        </Col>
      </Row>
    </>
  );
};

/**
 * Displays a grid of role selection tiles for the contact form. For new contacts, shows all possible roles.
 * For existing contacts, shows only the current role and displays a warning message.
 * @param {Object} contactFormData - Current form data for the contact.
 * @param {Function} onSetContactFormData - Callback to update contact form data.
 * @param {Object} errors - Validation errors object.
 * @param {boolean} isFromPropertyDeveloper - Flag indicating if form is accessed from property developer context.
 */
const RoleTileSelector = ({
  contactFormData,
  onSetContactFormData,
  errors,
  isFromPropertyDeveloper,
}: ContactFormContentProps) => {
  // For existing contacts, only show their current role.
  const userTypeForExistingContact: string[] = useMemo(() => {
    return USER_TYPES.filter(
      (userType: string) => userType === contactFormData.userRole
    );
  }, [contactFormData.userRole]);

  /**
   * Handles role selection with appropriate validations.
   * @param {string} userType - The selected role type.
   */
  const handleRoleSelection = (userType: any) => {
    // No action needed if selecting the already-selected role.
    if (userType === contactFormData.userRole) {
      return;
    }

    // Enforce property developer restriction when in property developer context.
    if (isFromPropertyDeveloper && userType !== 'Property Developer') {
      return toast.error(
        'Only Property Developer can be added from Add Property Developer',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    }

    // Update form data with the new role and reset related fields.
    onSetContactFormData({
      userRole: userType,
      propertyId: '',
      suiteId: '',
      Suites: [],
      serviceType: '',
      servicesProvided:
        contactFormData.userRole === userType
          ? contactFormData.servicesProvided
          : [],
      isPrimaryContact: false,
    });
  };

  // Determine if we're editing an existing contact.
  const isExistingAccount: boolean = !!contactFormData?.id;

  // Show all roles for new contacts, but only current role for existing ones.
  const userTypesToShow: string[] = isExistingAccount
    ? userTypeForExistingContact
    : USER_TYPES;

  return (
    <>
      {/* Validation error message area. */}
      <div className="input__error">{errors.userRole}</div>
      <Heading>Role</Heading>
      {/* Warning message displayed only when editing existing contacts. */}
      {isExistingAccount && (
        <div
          style={{
            color: '#d0285b',
            marginBottom: '2rem',
            fontSize: '1.5rem',
            fontStyle: 'italic',
          }}
        >
          This cannot be modified after a user is created. If you need to change
          it, please create a new contact or contact Juniper support.
        </div>
      )}
      {/* Role selection grid - disabled for existing contacts. */}
      <div style={{ pointerEvents: contactFormData?.id ? 'none' : undefined }}>
        <Row granola>
          {userTypesToShow.map((userType: string) => (
            <Col granola key={userType} xs="6" md="4">
              <Tile
                title={
                  userType === 'Property Developer'
                    ? variables.propertyDeveloper
                    : userType === 'Home Owner'
                      ? variables.homeOwner
                      : userType
                }
                size="small"
                active={userType === contactFormData.userRole}
                onClick={() => handleRoleSelection(userType)}
                icon={<IcoMoon glyph={ICONS[userType]} />}
              />
            </Col>
          ))}
        </Row>
      </div>
    </>
  );
};

// Internal component for company details.
const CompanyDetails = ({
  contactFormData,
  onSetContactFormData,
}: ContactFormContentProps) => {
  // Only render for certain roles.
  if (
    contactFormData.userRole !== 'Service Provider' &&
    contactFormData.userRole !== 'Strata Manager' &&
    contactFormData.userRole !== 'Vendor'
  ) {
    return null;
  }

  return (
    <>
      <Row granola style={{ paddingTop: '3rem' }}>
        <Col xs="12" md="12">
          <Input
            value={contactFormData.companyName}
            onChange={(e: any) =>
              onSetContactFormData({ companyName: e.target.value })
            }
            label="Company name"
            placeholder="Company name"
            block
            required={contactFormData?.userRole === 'Service Provider'}
          />
        </Col>
      </Row>
    </>
  );
};

// Internal component for property and suite selection.
const PropertySuiteSelector = ({
  contactFormData,
  onSetContactFormData,
  properties,
  filteredSuites,
  errors,
  propertiesList,
  suitesList,
  user,
  onFetchPropertySuites,
  clearSuites,
  selectionForProperties,
  filterList,
}: ContactFormContentProps) => {
  const { userRole }: ContactFormData = contactFormData;

  const PROPERTY_SUITE_ROLES = [
    'Home Owner',
    'Realtor',
    'Occupant',
    'Service Manager',
    'Service Provider',
  ];
  const MULTI_SELECT_ROLES = ['Home Owner', 'Realtor', 'Service Provider'];
  const UNIT_SELECTION_ROLES = ['Home Owner', 'Realtor'];

  // Check role against defined groups.
  const isApplicableRole: boolean = PROPERTY_SUITE_ROLES.includes(userRole);
  const usesMultiSelect: boolean = MULTI_SELECT_ROLES.includes(userRole);
  const showsUnitSelection: boolean = UNIT_SELECTION_ROLES.includes(userRole);
  const showsSuiteDropdown: boolean = userRole === 'Occupant';

  // Early return if role doesn't need property/suite selection.
  if (!isApplicableRole) {
    return null;
  }

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      {/* Error display */}
      <div className="input__errors">
        <div className="input__error">{errors.type}</div>
      </div>
      {/* Property Selection - either MultiSelect or Dropdown based on role */}
      {usesMultiSelect ? (
        <>
          {/* Multi-select properties for Home Owner, Realtor, Service Provider. */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="reporting__heading" style={{ color: 'black' }}>
              Properties
            </div>
            {userRole !== 'Service Provider' && (
              <div className="picker__required" />
            )}
          </div>
          <Multiselect
            onRemove={(e: any) => selectionForProperties(e, 'remove')}
            onSelect={(e: any) => selectionForProperties(e, 'add')}
            key="Id"
            selectedValues={propertiesList}
            options={properties.map((property: Property) => ({
              Id: property.propertyId,
              name: property.name,
            }))}
            label="Select Properties"
            displayValue="name"
            placeholder="Select Properties"
            block
          />
        </>
      ) : (
        // Single property dropdown for Occupant and Service Manager.
        <Select
          value={contactFormData.propertyId ? contactFormData.propertyId : ''}
          onChange={(e: any) => {
            clearSuites();
            onFetchPropertySuites(
              e.target.value,
              user.id,
              user.roles[0].role.name
            );
            onSetContactFormData({
              propertyId: e.target.value,
              suiteId: '',
              Suites: [],
            });
          }}
          options={properties.map((property: Property) => ({
            value: property.propertyId,
            label: property.name,
          }))}
          label="Property"
          placeholder="Property"
          block
        />
      )}
      {/* Unit Selection for applicable roles. */}
      {showsUnitSelection && (
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="reporting__heading" style={{ color: 'black' }}>
              Unit(s)
            </div>
            {userRole !== 'Service Provider' && (
              <div className="picker__required" />
            )}
          </div>
          <Multiselect
            id="suites"
            options={filteredSuites.map((suite: DetailedSuite) => ({
              Id: suite.id,
              name: suite.propertyName + ' ' + '-' + ' ' + suite.number,
              propertyId: suite.propertyId,
            }))}
            selectedValues={suitesList}
            displayValue="name"
            placeholder={`Select ${variables.suiteName}`}
            disable={filteredSuites.length === 0}
            onSelect={(e: any) => filterList(e)}
            onRemove={(e: any) => filterList(e)}
          />
        </>
      )}
      {/* Suite Selection for Occupant. */}
      {showsSuiteDropdown && (
        <Select
          value={contactFormData.suiteId}
          onChange={(e) => onSetContactFormData({ suiteId: e.target.value })}
          options={filteredSuites.map((suite: DetailedSuite) => ({
            value: suite.id,
            label: suite.number,
          }))}
          label={
            variables.Suite === 'Suite'
              ? variables.Suite + ' Number'
              : variables.Suite + ''
          }
          placeholder={
            variables.Suite === 'Suite'
              ? variables.Suite + ' Number'
              : variables.Suite + ''
          }
          block
        />
      )}
    </div>
  );
};

// Internal component for service type selection.
const ServiceTypeSelector = ({
  contactFormData,
  onSetContactFormData,
  types,
}: ContactFormContentProps) => {
  // Only render for certain roles.
  if (
    contactFormData.userRole !== 'Service Provider' &&
    contactFormData.userRole !== 'Vendor'
  ) {
    return null;
  }

  return (
    <>
      <Heading>
        Service Provided <span style={{ color: 'red' }}>*</span>
        <div
          style={{
            paddingTop: '1rem',
            color: '#454F5B',
            fontWeight: '620',
          }}
        >
          Please select all that apply
        </div>
      </Heading>
      {/* Service types grid. */}
      <Row granola>
        {types.map((issueType: ServiceType, i: number) => (
          <Col granola key={i} md="4" xs="6">
            <Tile
              icon={<Icon weight="light" glyph={ICONS[issueType.name]} />}
              active={
                contactFormData.servicesProvided != null
                  ? contactFormData.servicesProvided.includes(issueType.name)
                  : false
              }
              onClick={() =>
                contactFormData.servicesProvided != null
                  ? contactFormData.servicesProvided.includes(issueType.name)
                    ? onSetContactFormData({
                        servicesProvided:
                          contactFormData.servicesProvided.filter(
                            (i: string) => i !== issueType.name
                          ),
                      })
                    : onSetContactFormData({
                        servicesProvided: [
                          ...contactFormData.servicesProvided,
                          issueType.name,
                        ],
                      })
                  : onSetContactFormData({
                      servicesProvided: [issueType.name],
                    })
              }
              title={
                issueType.name === 'Heating/Cooling'
                  ? `${issueType.name.substring(
                      0,
                      issueType.name.lastIndexOf('/')
                    )} / ${issueType.name.substring(
                      issueType.name.lastIndexOf('/') + 1,
                      issueType.name.length
                    )}`
                  : issueType.name
              }
              size="tiny"
            />
          </Col>
        ))}
      </Row>
    </>
  );
};

// Internal component for additional contact options.
const ContactOptions = ({
  contactFormData,
  onSetContactFormData,
  onErrorEmpty,
  isFromPropertyDeveloper,
  contactType,
}: ContactFormContentProps) => {
  // Calendar link for Service Provider and Property Developer.
  const showCalendarSection: boolean =
    contactFormData.userRole === 'Service Provider' ||
    contactFormData.userRole === 'Property Developer';

  // Primary contact toggle for Occupant and Home Owner.
  const showPrimaryContactToggle: boolean =
    contactFormData.userRole === 'Occupant' ||
    contactFormData.userRole === 'Home Owner';

  // Dashboard access toggle for certain roles.
  const showDashboardAccessToggle: boolean =
    contactFormData?.userRole?.toUpperCase() ===
      USER_ROLES.OCCUPANT.toUpperCase() ||
    contactFormData?.userRole?.toUpperCase() ===
      USER_ROLES.HOME_OWNER.toUpperCase() ||
    contactFormData?.userRole?.toUpperCase() ===
      USER_ROLES.REALTOR.toUpperCase();

  // If no options to show, don't render anything.
  if (
    !showCalendarSection &&
    !showPrimaryContactToggle &&
    !showDashboardAccessToggle
  ) {
    return null;
  }

  return (
    <>
      {/* Calendar Link. */}
      {showCalendarSection && (
        <div style={{ paddingTop: '2rem' }}>
          <Input
            value={
              contactFormData && contactFormData.calendarLink
                ? contactFormData.calendarLink
                : ''
            }
            onChange={(e: any) => {
              onSetContactFormData({ calendarLink: e.target.value });
              onErrorEmpty('calendarLink');
            }}
            label="Your Calendaring Link"
            placeholder="Calendar Link"
            block
          />
          {/* Primary contact toggle for Property Developer. */}
          {isFromPropertyDeveloper && isFromPropertyDeveloper === true && (
            <Toggle
              label={
                'Make ' +
                contactFormData.userRole +
                ' the primary contact for the Property?'
              }
              value={contactFormData.isPrimaryPropertyDeveloperContact}
              onToggle={(value: boolean) =>
                onSetContactFormData({
                  isPrimaryPropertyDeveloperContact:
                    !contactFormData.isPrimaryPropertyDeveloperContact,
                })
              }
            />
          )}
        </div>
      )}
      {/* Primary contact toggle. */}
      {showPrimaryContactToggle && (
        <div style={{ marginTop: 10 }}>
          <Toggle
            label={
              'Make ' +
              contactType(contactFormData.userRole) +
              ' the primary contact for the ' +
              variables.suite +
              '?'
            }
            value={contactFormData.isPrimaryContact}
            onToggle={(value: boolean) =>
              onSetContactFormData({
                isPrimaryContact: !contactFormData.isPrimaryContact,
              })
            }
          />
        </div>
      )}
      {/* Dashboard access toggle. */}
      {showDashboardAccessToggle && (
        <div style={{ marginTop: 10 }}>
          <Toggle
            label={`The ${contactFormData?.userRole} can only view the dashboard content`}
            value={contactFormData.isDashboardAccess}
            onToggle={() =>
              onSetContactFormData({
                isDashboardAccess: !contactFormData.isDashboardAccess,
              })
            }
          />
        </div>
      )}
    </>
  );
};

// Main ContactFormContent component that renders the form fields organized into logical sections.
const ContactFormContent = (props: ContactFormContentProps) => {
  const { propertySuites }: ContactFormContentProps = props;

  return (
    <>
      <Row chonky>
        {/* Left Column - Contact Information. */}
        <Col chonky xs="12" md="6">
          <ContactBasicInfo {...props} />
          <ContactAddress {...props} />
        </Col>
        {/* Right Column - Role-specific information. */}
        <Col chonky xs="12" md="6">
          <RoleTileSelector {...props} />
          <CompanyDetails {...props} />
          <PropertySuiteSelector {...props} filteredSuites={propertySuites} />
          <ServiceTypeSelector {...props} />
          <ContactOptions {...props} />
        </Col>
      </Row>
    </>
  );
};

export default ContactFormContent;
